import { isEmpty, get } from 'lodash';

import { alertError, alertSuccess } from './alert';
import api from '../../utils/api';
import { CHANNELS as CHANNELS_TYPES } from '../../constants/channels';
import { CHANNELS, CHANNELS_SET_DATA, CHANNELS_SET_LOADING, CHANNELS_SET_CONFIGURATION } from '../reducers/channels';
import history from '../../utils/history';
import { getRetailer } from './retailer';

export const setChannels = (channels) => ({ type: CHANNELS, payload: channels });

export const getChannels = () => async (dispatch) => {
  try {
    const params = new URLSearchParams();
    params.append('limit', 200);
    const response = await api(`/channels?` + params.toString());

    if (response.data) {
      dispatch(setChannels(response.data));
    } else {
      dispatch(alertError('Error'));
    }
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const getChannelRedirectUri = (channel) => async (dispatch, getState) => {
  const { supplierId } = getState().role;
  const url = `/suppliers/${supplierId}/channels/oauth`;
  const params = new URLSearchParams({ channel_id: channel.id });
  const additionalUrlQueryParams = get(channel, 'additionalUrlQueryParams', {});

  if (!isEmpty(additionalUrlQueryParams)) {
    for (const key in additionalUrlQueryParams) {
      params.append(key, additionalUrlQueryParams[key]);
    }
  }

  try {
    const response = await api(url + '?' + params.toString());
    if (response.data) {
      window.location.href = response.data.authorization_url;
    } else {
      dispatch(alertError('Error'));
    }
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const postChannelOauth = (authQuery) => async (dispatch, getState) => {
  let supplierId;

  try {
    const state = JSON.parse(authQuery.get('state'));
    supplierId = state.supplier_id;
  } catch (error) {
    supplierId = getState().role.supplierId;
  }

  try {
    const jsonParams = Object.fromEntries(authQuery.entries());
    const response = await api.post(`/suppliers/${supplierId}/channels/oauth`, jsonParams);

    if (response?.data) {
      const retailerId = response.data.retailer_id;
      const retailerName = response.data.retailer_name;
      dispatch(alertSuccess(`You have successfully connected ${retailerName}`));
      redirectToSupplierRetailer(supplierId, retailerId);
    } else {
      dispatch(alertError(response?.data?.message ?? 'Error connecting channel'));
      redirectToSupplierRetailersPage(supplierId);
    }
  } catch (error) {
    dispatch(alertError(error.response));
    redirectToSupplierRetailersPage(supplierId);
  }
};

export const tiktokListWarehouses =
  ({ supplierId, retailerId }) =>
  async (dispatch) => {
    dispatch({ type: CHANNELS_SET_LOADING, payload: { loading: { listWarehouses: true } } });
    try {
      const response = await api.get(`/integrations/tiktok/warehouses`, {
        params: {
          supplier_id: supplierId,
          retailer_id: retailerId,
        },
      });

      if (response?.data?.success) {
        const { warehouses } = response.data;
        dispatch({
          type: CHANNELS_SET_DATA,
          payload: { integrationType: CHANNELS_TYPES.TIKTOK, data: { warehouses } },
        });
      } else {
        dispatch(alertError(response?.data?.message ?? 'Error fetching warehouses'));
      }
    } catch (error) {
      dispatch(alertError(error));
    } finally {
      dispatch({ type: CHANNELS_SET_LOADING, payload: { loading: { listWarehouses: false } } });
    }
  };

export const tiktokPostWarehouseId =
  ({ selected, retailerId, supplierId }) =>
  async (dispatch) => {
    dispatch({ type: CHANNELS_SET_LOADING, payload: { loading: { postWarehouse: true } } });
    try {
      const response = await api.post(`/integrations/tiktok/warehouse`, {
        retailer_id: retailerId,
        supplier_id: supplierId,
        warehouse_id: selected,
      });

      if (response?.data?.success) {
        dispatch(alertSuccess('Warehouse connected successfully'));
        dispatch({
          type: CHANNELS_SET_CONFIGURATION,
          payload: {
            integrationType: CHANNELS_TYPES.TIKTOK,
            data: {
              hasWarehouseId: true,
            },
          },
        });
      } else {
        dispatch(alertError(response?.data?.message ?? 'Error setting warehouses'));
      }
    } catch (error) {
      dispatch(alertError(error));
    } finally {
      dispatch({ type: CHANNELS_SET_LOADING, payload: { loading: { postWarehouse: false } } });
    }
  };

export const tiktokListBrands =
  ({ supplierId, retailerId }) =>
  async (dispatch) => {
    dispatch({ type: CHANNELS_SET_LOADING, payload: { loading: { listBrands: true } } });
    try {
      const response = await api.get(`/integrations/tiktok/brands`, {
        params: {
          supplier_id: supplierId,
          retailer_id: retailerId,
        },
      });

      if (response?.data?.success) {
        const { brands } = response.data;
        dispatch({ type: CHANNELS_SET_DATA, payload: { integrationType: CHANNELS_TYPES.TIKTOK, data: { brands } } });
      } else {
        dispatch(alertError(response?.data?.message ?? `Error fetching brands`));
      }
    } catch (error) {
      dispatch(alertError(error));
    } finally {
      dispatch({ type: CHANNELS_SET_LOADING, payload: { loading: { listBrands: false } } });
    }
  };

export const tiktokListProductDesigners =
  ({ supplierId }) =>
  async (dispatch) => {
    dispatch({ type: CHANNELS_SET_LOADING, payload: { loading: { listDesigners: true } } });
    try {
      const response = await api.get(`/integrations/tiktok/product-designers`, {
        params: {
          supplier_id: supplierId,
        },
      });

      if (response?.data?.success) {
        const { designers } = response.data;
        dispatch({ type: CHANNELS_SET_DATA, payload: { integrationType: CHANNELS_TYPES.TIKTOK, data: { designers } } });
      } else {
        dispatch(alertError(response?.data?.message ?? 'Error fetching designers'));
      }
    } catch (error) {
      dispatch(alertError(error));
    } finally {
      dispatch({ type: CHANNELS_SET_LOADING, payload: { loading: { listDesigners: false } } });
    }
  };

export const tiktokPostBrandIdMapping =
  ({ supplierId, retailerId, mapping }) =>
  async (dispatch) => {
    dispatch({ type: CHANNELS_SET_LOADING, payload: { loading: { postBrandIdMapping: true } } });
    try {
      const response = await api.post(`/integrations/tiktok/brand-id-mapping`, {
        retailer_id: retailerId,
        supplier_id: supplierId,
        mapping,
      });
      if (response?.data?.success) {
        dispatch({
          type: CHANNELS_SET_CONFIGURATION,
          payload: {
            integrationType: CHANNELS_TYPES.TIKTOK,
            data: {
              hasBrandIdMapping: true,
            },
          },
        });
        dispatch({
          type: CHANNELS_SET_DATA,
          payload: {
            integrationType: CHANNELS_TYPES.TIKTOK,
            data: {
              brandIdMapping: mapping,
            },
          },
        });
        if (!isEmpty(mapping)) {
          dispatch(alertSuccess('Brand mapping successfully updated'));
        }
      } else {
        dispatch(alertError(response?.data?.message ?? 'Error updating brand mapping'));
      }
    } catch (error) {
      dispatch(alertError(error));
    } finally {
      dispatch({ type: CHANNELS_SET_LOADING, payload: { loading: { postBrandIdMapping: false } } });
    }
  };

export const miraklListReturnCodes =
  ({ supplierId, retailerId }) =>
  async (dispatch) => {
    dispatch({ type: CHANNELS_SET_LOADING, payload: { loading: { listReturnCodes: true } } });
    try {
      const response = await api.get(`/integrations/mirakl/return-codes`, {
        params: {
          supplier_id: supplierId,
          retailer_id: retailerId,
        },
      });

      if (response?.data?.success) {
        const { return_codes: returnCodes } = response.data;
        dispatch({
          type: CHANNELS_SET_DATA,
          payload: { integrationType: CHANNELS_TYPES.MIRAKL, data: { returnCodes } },
        });
      } else {
        dispatch(alertError(response?.data?.message ?? 'Error fetching return codes'));
      }
    } catch (error) {
      dispatch(alertError(error));
    } finally {
      dispatch({ type: CHANNELS_SET_LOADING, payload: { loading: { listReturnCodes: false } } });
    }
  };

export const miraklPostReturnCode =
  ({ supplierId, retailerId, returnCode }) =>
  async (dispatch) => {
    dispatch({ type: CHANNELS_SET_LOADING, payload: { loading: { postReturnCode: true } } });
    try {
      const response = await api.post(`/integrations/mirakl/return-codes`, {
        retailer_id: retailerId,
        supplier_id: supplierId,
        return_code: returnCode,
      });
      if (response?.data?.success) {
        dispatch({
          type: CHANNELS_SET_CONFIGURATION,
          payload: { integrationType: CHANNELS_TYPES.MIRAKL, data: { hasReturnCode: true } },
        });
        dispatch(alertSuccess('Return code successfully updated'));
      } else {
        dispatch(alertError(response?.data?.message ?? 'Error updating return code'));
      }
    } catch (error) {
      dispatch(alertError(error));
    } finally {
      dispatch({ type: CHANNELS_SET_LOADING, payload: { loading: { postReturnCode: false } } });
    }
  };

export const redirectToSupplierRetailer = (supplierId, retailerId) =>
  history.push(`/suppliers/${supplierId}/retailers/${retailerId}`);

const redirectToSupplierRetailersPage = (supplierId) => {
  setTimeout(() => {
    history.push(`/suppliers/${supplierId}/retailers`);
  }, 2000);
};

export const toggleTiktokStatus = (connectionStatus, retailer_id, supplier_id) => async (dispatch) => {
  try {
    const res = await api.post(`/integrations/tiktok/toggle-connection-status`, {
      retailer_id,
      supplier_id,
      connection_status: connectionStatus,
    });

    if (res.data.success) {
      dispatch(getRetailer(retailer_id, false));
      dispatch(alertSuccess('Updated tiktok status successfully.'));
    } else dispatch(alertError('Failed to update tiktok status.'));
  } catch (e) {
    dispatch(alertError(e));
  }
};
